import { useCallback, useRef } from 'react';
import generateCSV from '@/utilities/createCSV';

export const useAdminExceptionHooks = ({ where, exceptions, order_by }, { fetchMore, setHasMore, retrieveCsv }) => {
    const loadMoreInFlightRef = useRef(false);
    const PAGE_SIZE = 100;

    const loadMore = useCallback(() => {
        if (exceptions.length === 0 || loadMoreInFlightRef.current) return;
        loadMoreInFlightRef.current = true;

        fetchMore({
            variables: {
                where: { _and: where },
                order_by,
                offset: exceptions.length,
                limit: PAGE_SIZE,
            },
            updateQuery: (data, { fetchMoreResult }) => {
                const prev = Object.fromEntries((data.results || []).map((exception) => [exception?.mapping_id, true]));
                const incoming = (fetchMoreResult.results || []).filter((exception) => !prev[exception?.mapping_id]);
                const clone = [...(data.results || []), ...incoming];

                if (incoming.length === 0) {
                    setHasMore(false);
                }
                return {
                    results: clone,
                };
            },
        })
            .then((result) => {
                if ((result?.data?.results || []).length < PAGE_SIZE) {
                    setHasMore(false);
                }
            })
            .catch(() => {
                setHasMore(false);
            })
            .finally(() => {
                loadMoreInFlightRef.current = false;
            });
    }, [where, order_by, fetchMore, setHasMore, exceptions.length]);

    const exportCSV = useCallback(() => {
        return retrieveCsv({ query: { _and: where } }).then((result) => {
            const TODAY = new Date();
            const blob = new Blob([result.data]);

            const filename = `Exceptions ${TODAY.getTime()}.csv`;
            const autoclick = document.createElement('a');
            const payload = URL.createObjectURL(blob);
            autoclick.setAttribute('href', payload);
            autoclick.setAttribute('download', filename);
            autoclick.style.visibility = 'hidden';
            document.body.appendChild(autoclick);
            autoclick.click();
            document.body.removeChild(autoclick);
        });
    }, [where, retrieveCsv]);

    return {
        exportCSV,
        loadMore,
    };
};
